// eslint-disable-next-line @nx/enforce-module-boundaries
import { type RateSheetDraft } from '@ra-mr/types';

const editableLineFields = [
  'pol',
  'pod',
  'originGeoObjectId',
  'destinationGeoObjectId',
  'containerType',
  'containerSizeId',
  'amount',
  'currency',
  'incoterm',
  'transportMode',
  'carrier',
  'carrierId',
  'transitTimeDays',
  'etdDates',
  'etdUnresolvedText',
  'validFrom',
  'validUntil',
  'notes',
  'sourceReference',
] as const;

export type RateSheetEditableLineField = (typeof editableLineFields)[number];

export type RateSheetDraftChange =
  | {
      kind: 'sheet';
      field: 'providerName' | 'notes' | 'providerId';
      before: string | null;
      after: string | null;
    }
  | {
      kind: 'line';
      rowIndex: number;
      beforeRowIndex: number | null;
      afterRowIndex: number | null;
      field: RateSheetEditableLineField;
      before: string | number | string[] | null;
      after: string | number | string[] | null;
    }
  | {
      kind: 'row_added' | 'row_removed';
      rowIndex: number;
    };

/** Persisted edits only: warnings, hydrated labels and AI explanations are advisory. */
export const getRateSheetDraftChanges = (
  current: RateSheetDraft,
  proposed: RateSheetDraft,
  providerId?: string | null,
  rowOrigins?: Array<number | null>
): RateSheetDraftChange[] => {
  const changes: RateSheetDraftChange[] = [];
  for (const field of ['providerName', 'notes'] as const) {
    const before = current[field] ?? null;
    const after = proposed[field] ?? null;
    if (before !== after) {
      changes.push({ kind: 'sheet', field, before, after });
    }
  }
  // Acceptance can link an unlinked sheet, but cannot replace an existing link.
  if (!providerId && proposed.providerMatch?.providerId) {
    changes.push({
      kind: 'sheet',
      field: 'providerId',
      before: null,
      after: proposed.providerMatch.providerId,
    });
  }

  const origins = resolveRowOrigins(current, proposed, rowOrigins);
  const matched = new Set(origins.filter((index) => { return index !== null; }));
  const pairs: Array<{ beforeRowIndex: number | null; afterRowIndex: number | null }> =
    origins.map((beforeRowIndex, afterRowIndex) => { return { beforeRowIndex, afterRowIndex }; });
  current.lines.forEach((_line, beforeRowIndex) => {
    if (!matched.has(beforeRowIndex)) {
      pairs.push({ beforeRowIndex, afterRowIndex: null });
    }
  });
  for (const { beforeRowIndex, afterRowIndex } of pairs) {
    const rowIndex = afterRowIndex ?? beforeRowIndex!;
    const beforeLine = beforeRowIndex === null ? undefined : current.lines[beforeRowIndex];
    const afterLine = afterRowIndex === null ? undefined : proposed.lines[afterRowIndex];
    if (!beforeLine || !afterLine) {
      changes.push({ kind: afterLine ? 'row_added' : 'row_removed', rowIndex });
    }
    for (const field of editableLineFields) {
      if (field === 'etdDates') {
        const before = [...new Set(beforeLine?.etdDates ?? [])].sort();
        const after = [...new Set(afterLine?.etdDates ?? [])].sort();
        const unchanged =
          before.length === after.length &&
          before.every((date, index) => {
            return date === after[index];
          });
        if (!unchanged) {
          changes.push({ kind: 'line', rowIndex, beforeRowIndex, afterRowIndex, field, before, after });
        }
        continue;
      }
      if (field === 'etdUnresolvedText') {
        const before = beforeLine?.etdUnresolvedText?.trim() || null;
        const after = afterLine?.etdUnresolvedText?.trim() || null;
        if (before !== after) {
          changes.push({ kind: 'line', rowIndex, beforeRowIndex, afterRowIndex, field, before, after });
        }
        continue;
      }
      const before = beforeLine?.[field] ?? null;
      const after = afterLine?.[field] ?? null;
      if (before !== after) {
        changes.push({ kind: 'line', rowIndex, beforeRowIndex, afterRowIndex, field, before, after });
      }
    }
  }
  return changes;
};

/** Validate application-produced correspondence before using it for review. */
const resolveRowOrigins = (
  current: RateSheetDraft,
  proposed: RateSheetDraft,
  supplied?: Array<number | null>
): Array<number | null> => {
  let previous = -1;
  if (supplied?.length === proposed.lines.length && supplied.every((index) => {
    if (index === null) {
      return true;
    }
    if (!Number.isInteger(index) || index <= previous || index >= current.lines.length) {
      return false;
    }
    previous = index;
    return true;
  })) {
    return supplied;
  }

  // Legacy proposals have no correspondence. Keep positional behavior for ordinary
  // edits; align differing row counts using unchanged rows as anchors.
  const beforeCount = current.lines.length;
  const afterCount = proposed.lines.length;
  if (beforeCount === afterCount || beforeCount > 2000 || afterCount > 2000) {
    return proposed.lines.map((_line, index) => { return index < beforeCount ? index : null; });
  }
  const signature = (line: RateSheetDraft['lines'][number]): string => {
    return JSON.stringify(editableLineFields.map((field) => {
      if (field === 'etdDates') {
        return [...new Set(line.etdDates ?? [])].sort();
      }
      if (field === 'etdUnresolvedText') {
        return line.etdUnresolvedText?.trim() || null;
      }
      return line[field] ?? null;
    }));
  };
  const before = current.lines.map(signature);
  const after = proposed.lines.map(signature);
  const costs = Array.from({ length: beforeCount + 1 }, () => { return new Uint16Array(afterCount + 1); });
  for (let i = beforeCount; i >= 0; i--) {
    for (let j = afterCount; j >= 0; j--) {
      if (i === beforeCount) {
        costs[i][j] = afterCount - j;
      } else if (j === afterCount) {
        costs[i][j] = beforeCount - i;
      } else {
        costs[i][j] = Math.min(costs[i + 1][j + 1] + Number(before[i] !== after[j]),
          1 + costs[i + 1][j], 1 + costs[i][j + 1]);
      }
    }
  }
  const origins: Array<number | null> = [];
  let i = 0;
  let j = 0;
  while (j < afterCount) {
    if (i < beforeCount && costs[i][j] === costs[i + 1][j + 1] + Number(before[i] !== after[j])) {
      origins.push(i++);
      j++;
    } else if (i < beforeCount && costs[i][j] === 1 + costs[i + 1][j]) {
      i++;
    } else {
      origins.push(null);
      j++;
    }
  }
  return origins;
};
